import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { client, getAccessToken } from '../client'
import { queryKeys } from '../keys'
import type { components } from '@/lib/generated/api-types'
import { API_BASE_URL } from '@/lib/config'

// Type exports for convenience
export type Vehicle = components['schemas']['Vehicle']
export type VehicleDetailed = components['schemas']['VehicleDetailed']
export type GetFreeVehiclesResult =
  components['schemas']['GetFreeVehiclesResult']

// Get all available vehicles
export function useVehicles(options?: {
  enabled?: boolean
  refetchInterval?: number | false
}) {
  return useQuery({
    queryKey: queryKeys.vehicles.all,
    queryFn: async () => {
      const { data, error } = await client.GET('/api/v1/vehicles')
      if (error) throw error
      return data
    },
    staleTime: 15 * 1000, // 15 seconds - vehicles change frequently
    refetchInterval: options?.refetchInterval ?? 15 * 1000,
    enabled: options?.enabled ?? true,
  })
}

// WebSocket vehicle stream — writes directly to vehicles.all query cache
export function useVehicleStream(options: {
  enabled: boolean
  active?: boolean
}) {
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!options.enabled || typeof window === 'undefined') return

    let cancelled = false

    const connect = async () => {
      if (cancelled) return

      const token = await getAccessToken()
      if (!token) return

      const active = options.active ?? true
      const wsBase = API_BASE_URL.replace(/^http/, 'ws')
      const url = `${wsBase}/api/v1/vehicles/stream?token=${encodeURIComponent(token)}&active=${active}`
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          queryClient.setQueryData(queryKeys.vehicles.all, data)
        } catch {
          // ignore malformed messages
        }
      }

      ws.onclose = (event) => {
        wsRef.current = null
        // 4001 = auth failure, don't retry
        if (event.code === 4001 || cancelled) return
        // Reconnect after delay
        reconnectTimerRef.current = setTimeout(connect, 3000)
      }

      ws.onerror = () => {
        // onclose will fire after onerror, handled there
      }
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [options.enabled, options.active, queryClient])
}

// Get detailed vehicle information
export function useVehicle(vehicleId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.vehicles.detail(vehicleId!),
    queryFn: async () => {
      const { data, error } = await client.GET(
        '/api/v1/vehicles/{vehicle_id}',
        {
          params: { path: { vehicle_id: vehicleId! } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!vehicleId,
    staleTime: 60 * 1000, // 1 minute
  })
}

// Get accessories list
export function useAccessories() {
  return useQuery({
    queryKey: queryKeys.accessories,
    queryFn: async () => {
      const { data, error } = await client.GET('/api/v1/accessories')
      if (error) throw error
      return data
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - accessories rarely change
  })
}

// Get available vehicle models
export function useVehicleModels() {
  return useQuery({
    queryKey: queryKeys.vehicleModels,
    queryFn: async () => {
      const { data, error } = await client.GET('/api/v1/vehicle-models')
      if (error) throw error
      return data
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - models rarely change
  })
}
